<!-- src/components/SiteList.vue -->
<template>
    <div class="site-list">
        <!-- draggable 网格容器 -->
        <draggable
            tag="div"
            class="grid-container"
            :list="sitesList"
            @end="onDragEnd"
            handle=".drag-handle"
            :animation="200"
            item-key="siteId"
            group="sites"
        >
            <template #item="{ element: site }">
                <SiteItem
                    :key="site.siteId"
                    :site="site"
                    :sectionId="sectionId"
                    :cardStyles="cardStyles"
                />
            </template>
        </draggable>

        <!-- 添加站点按钮 - 移出网格，作为独立的底部操作 -->
        <div class="add-site-footer">
            <div class="add-btn-wrapper" @click="showAddSiteModal">
                <icon-plus />
                <span>添加站点</span>
            </div>
        </div>

        <!-- 添加站点的模态框 -->
        <a-modal
            v-model:visible="addSiteModalVisible"
            title="添加站点"
            :width="380"
            :footer="false"
            class="glass-modal-custom"
            :mask-style="{ backdropFilter: 'blur(8px)', background: 'rgba(0, 0, 0, 0.4)' }"
            :modal-style="{ background: 'transparent', boxShadow: 'none' }"
            :align-center="true"
        >
            <div class="glass-modal-content">
                <a-form :model="newSite" layout="vertical" size="medium">
                    <a-form-item label="标题">
                        <a-input v-model="newSite.siteTitle" placeholder="请输入站点标题" allow-clear class="glass-input" />
                    </a-form-item>
                    <a-form-item label="URL">
                        <a-input v-model="newSite.siteUrl" placeholder="请输入站点URL" allow-clear class="glass-input" />
                    </a-form-item>
                    <a-form-item label="图标类型">
                        <a-radio-group v-model="newSite.iconType" type="button" class="glass-radio">
                            <a-radio value="system">系统图标</a-radio>
                            <a-radio value="custom">自定义图标</a-radio>
                        </a-radio-group>
                    </a-form-item>
                    <a-form-item label="图标路径" v-if="newSite.iconType === 'custom'" field="iconPath">
                        <a-space>
                            <a-input v-model="newSite.iconPath" :allow-clear="true" placeholder="URL 或 Base64" style="width: 240px" class="glass-input" />
                            <a-button @click="openIconLibrary" class="glass-btn-icon">
                                <template #icon><icon-search /></template>
                            </a-button>
                        </a-space>
                    </a-form-item>
                    <a-form-item label="排序序号" field="sortRank">
                        <a-input-number v-model="newSite.sortRank" :min="0" style="width: 100%" class="glass-input" />
                    </a-form-item>
                    <a-form-item>
                        <a-button type="primary" long @click="handleAddSite" class="glass-btn-primary">确定添加</a-button>
                    </a-form-item>
                </a-form>
            </div>
        </a-modal>
    </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { Message } from '@arco-design/web-vue';
import { IconPlus, IconSearch } from '@arco-design/web-vue/es/icon';
import SiteItem from './SiteItem.vue';
import draggable from 'vuedraggable';

export default defineComponent({
    name: 'SiteList',
    components: {
        SiteItem,
        draggable,
        IconPlus,
        IconSearch
    },
    props: {
        sites: {
            type: Array,
            required: true,
            default: () => [],
        },
        sectionId: {
            type: Number,
            required: true,
        },
    },
    setup(props) {
        const store = useStore();
        const addSiteModalVisible = ref(false);

        const newSite = ref({
            siteTitle: '',
            siteUrl: '',
            iconType: 'system',
            iconPath: '',
            verified: false,
            sortRank: 0,
        });

        const cardStyles = computed(() => store.state.sitesDiy.cardStyles);
        const sitesList = computed(() => props.sites);

        const addSite = (data) => store.dispatch('sites/addSite', data);
        const reorderSites = (data) => store.dispatch('sites/reorderSites', data);
        const fetchOrganizations = () => store.dispatch('organizations/fetchOrganizations');
        const fetchCardStyles = () => store.dispatch('sitesDiy/fetchCardStyles');

        const openIconLibrary = () => {
            window.open('https://icon-sets.iconify.design/', '_blank');
        };

        const showAddSiteModal = () => {
            addSiteModalVisible.value = true;
        };

        const handleIconTypeChange = (value) => {
            if (value === 'system') {
                newSite.value.iconPath = '';
            }
        };

        const handleAddSite = async () => {
            if (!newSite.value.siteTitle || !newSite.value.siteUrl) {
                Message.warning('请填写完整的站点信息');
                return;
            }

            if (newSite.value.iconType === 'system') {
                try {
                    // 特殊处理：飞书文档
                    if (newSite.value.siteUrl.includes('feishu.cn')) {
                        // 使用飞书开放平台 favicon
                        newSite.value.iconPath = 'https://open.feishu.cn/favicon.ico';
                    } else {
                        // 使用 Google Favicon API 获取高清图标 (sz=128)
                        newSite.value.iconPath = `https://www.google.com/s2/favicons?sz=128&domain_url=${encodeURIComponent(newSite.value.siteUrl)}`;
                    }
                } catch (e) {
                    newSite.value.iconPath = '/img/error.png';
                }
            }

            const siteData = {
                ...newSite.value,
                sectionId: props.sectionId,
            };

            try {
                await addSite(siteData);
                Message.success('站点添加成功');
                await fetchOrganizations();
                addSiteModalVisible.value = false;
                resetNewSite();
            } catch (error) {
                console.error('添加站点失败:', error);
                Message.error('添加站点失败');
            }
        };

        const resetNewSite = () => {
            newSite.value = {
                siteTitle: '',
                siteUrl: '',
                iconType: 'system',
                iconPath: '',
                verified: false,
                sortRank: 0,
            };
        };

        const onDragEnd = async () => {
            const reorderedSites = props.sites.map((site, index) => ({
                ...site,
                sortRank: index + 1,
            }));
            try {
                await reorderSites({ sectionId: props.sectionId, sites: reorderedSites });
                Message.success('站点排序已更新');
            } catch (error) {
                console.error('站点排序更新失败:', error);
                Message.error('站点排序更新失败');
            }
        };

        onMounted(() => {
            fetchCardStyles();
        });

        return {
            addSiteModalVisible,
            newSite,
            cardStyles,
            sitesList,
            openIconLibrary,
            showAddSiteModal,
            handleIconTypeChange,
            handleAddSite,
            onDragEnd,
        };
    }
});
</script>

<style scoped>
.site-list {
    padding: 5px;
    position: relative;
    width: 100%;
}

.grid-container {
    display: grid;
    /* 调整为 minmax(200px, 1fr)，在 1200px 容器下刚好一行 5 个，卡片会变小 */
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px; 
    transition: all 0.3s ease;
}

/* 移动端适配 */
@media (max-width: 768px) {
    .grid-container {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
}

/* 底部添加按钮样式 - 简约通透风格 */
.add-site-footer {
    display: flex;
    justify-content: center;
    margin-top: 16px;
    padding-bottom: 8px;
}

.add-btn-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    color: rgba(22, 93, 255, 0.8);
    font-size: 13px;
    cursor: pointer;
    border-radius: 12px;
    transition: all 0.2s;
    /* 移除背景色，仅靠边框和高光 */
    background: rgba(255, 255, 255, 0.1); 
    border: 1px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: none; /* 移除模糊 */
}

.add-btn-wrapper:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.6);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

/* 液体玻璃模态框样式 - 移除模糊 */
.glass-modal-content {
    background: linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.4) 100%);
    /* backdrop-filter: blur(25px); 移除模糊 */
    padding: 24px;
    border-radius: 20px;
    border: 1px solid rgba(255,255,255,0.6);
    border-top: 1px solid rgba(255,255,255,0.8);
    box-shadow: 0 25px 60px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(255,255,255,0.4);
}

/* 覆盖 Arco Input 样式 */
:deep(.glass-input .arco-input-wrapper), 
:deep(.glass-input .arco-input-number-wrapper) {
    background: rgba(255, 255, 255, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 8px;
    transition: all 0.3s;
}

:deep(.glass-input .arco-input-wrapper:focus-within) {
    background: rgba(255, 255, 255, 0.8);
    border-color: #165DFF;
    box-shadow: 0 0 0 3px rgba(22, 93, 255, 0.1);
}

:deep(.glass-btn-primary) {
    background: linear-gradient(135deg, #165DFF 0%, #0099FF 100%);
    border: none;
    box-shadow: 0 6px 16px rgba(22, 93, 255, 0.25);
    height: 36px;
    border-radius: 8px;
    font-weight: 500;
}

:deep(.glass-btn-primary:hover) {
    box-shadow: 0 8px 20px rgba(22, 93, 255, 0.35);
    transform: translateY(-1px);
}

:deep(.glass-btn-icon) {
    background: rgba(255,255,255,0.5);
    border: 1px solid rgba(255,255,255,0.6);
}
</style>
